import {
  Box,
  ClickAwayListener,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { VisualArticle } from 'components/domain/stockPricing/StockDisplay/VisualArticle'
import { HotkeyLegend } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { useHotkeyConfig, useScopelessHotkeys } from 'lib/hooks'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CardDataItem,
  CardLanguage,
  CountryEdition,
  Expansion,
  FINISH_ATTRIBUTES,
  FINISH_TYPE_LABELS,
  InventoryArticle,
  LocalizedCard,
  RichArticle,
  sleep,
  TAXATION_SCHEME,
  TAXATION_SCHEME_CONVERSIONS,
} from 'shared'
import { ARTICLE_EDIT } from 'utils/constants'
import { initializeFinishTypeAccordingCardNameOrFoil } from 'utils/lib'
import { v4 as uuid } from 'uuid'

export type ArticleEditHandleSave = (
  article: InventoryArticle | 'previous' | 'next',
  isCopied?: boolean
) => void

interface ArticleEditProps {
  addByExp?: boolean
  disableHotkeys: boolean
  template: InventoryArticle
  card: CardDataItem
  newPrice: number | undefined
  handleSave: ArticleEditHandleSave
  stopEdit: () => void
  onInputChange?: (newValue: RichArticle | null) => void
  showExistingStock?: boolean
  fixedLegendPosition?: [x: number, y: number]
  expansion: (Expansion & { name: string }) | null
  counter?: number
  setCounter?: (value: number) => void
  cardsInExpansion?: LocalizedCard[]
}

export const ArticleEdit = ({
  addByExp,
  disableHotkeys,
  template,
  card,
  newPrice,
  handleSave,
  stopEdit,
  onInputChange,
  showExistingStock = true,
  expansion,
  counter,
  setCounter,
  cardsInExpansion,
}: ArticleEditProps): JSX.Element => {
  const { t } = useTranslation()
  const hotkeys = useHotkeyConfig()

  const { palette, zIndex } = useTheme()

  const textFieldRef = useRef<HTMLInputElement | undefined>()

  const [hasFocus, setHasFocus] = useState(false)
  const [gradingActive, setGradingActive] = useState(false)
  const [disabledCommentTextfield, setDisabledCommentTextfield] = useState(false)
  const [disabledQuantityTextfield, setDisabledQuantityTextfield] = useState(false)
  const [articleIsGraded, setArticleIsGraded] = useState(false)

  const [selectedTaxationScheme, setSelectedTaxationScheme] = useState<TAXATION_SCHEME | ''>(
    template.taxationScheme ?? ''
  )

  const { user } = useUser()
  const templateValues = template

  const initialInputs = new RichArticle({
    ...templateValues,
    finishType:
      template.finishType !== FINISH_TYPE_LABELS.REGULAR
        ? template.finishType
        : initializeFinishTypeAccordingCardNameOrFoil(card),
    idArticle: templateValues.id ?? uuid(),
    marginTaxationQuantity:
      template.taxationScheme === TAXATION_SCHEME.MARGIN ? templateValues.quantity : 0,
    standardTaxationQuantity:
      template.taxationScheme === TAXATION_SCHEME.STANDARD ? templateValues.quantity : 0,
  })

  const [inputs, setInputs] = useState<RichArticle>(initialInputs)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setInputs(initialInputs)
    onInputChange?.(initialInputs)
  }, [card])

  useEffect(() => {
    textFieldRef.current?.focus()
  }, [])

  useEffect(() => {
    if (template.taxationScheme != null) {
      setSelectedTaxationScheme(template.taxationScheme)
    }
  }, [template.taxationScheme])

  useEffect(() => {
    if (articleIsGraded) {
      setDisabledQuantityTextfield(true)

      const newInputs = inputs.copy()

      newInputs.setAttribute('quantity', 1)

      setInputs(newInputs)
    }
  }, [articleIsGraded])

  useEffect(() => {
    if (gradingActive) {
      textFieldRef.current?.blur()
    } else {
      textFieldRef.current?.focus()
    }
  }, [gradingActive])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): Promise<void> | void => {
    const { name, type, value, checked } = e.target

    const newInputs = inputs.copy()

    const rawValue = type === 'checkbox' ? checked : value

    newInputs.setAttribute(name, rawValue)

    // If I'm changing the quantity, I also sync the taxation quantities
    if (name === 'quantity') {
      const qty = Math.ceil(Number(rawValue))
      if (!Number.isNaN(qty) && qty > 0) {
        const scheme =
          selectedTaxationScheme !== '' ? selectedTaxationScheme : template.taxationScheme

        if (scheme === TAXATION_SCHEME.STANDARD) {
          newInputs.standardTaxationQuantity = qty
          newInputs.marginTaxationQuantity = 0
        } else if (scheme === TAXATION_SCHEME.MARGIN) {
          newInputs.standardTaxationQuantity = 0
          newInputs.marginTaxationQuantity = qty
        }
      }
    }

    onInputChange?.(newInputs)
    setInputs(newInputs)
  }

  const hotkeyCallback = (e: KeyboardEvent, fn: () => void, force = false): void => {
    if ((!disableHotkeys && document.activeElement === textFieldRef.current) || force) {
      e.preventDefault()
      e.stopPropagation()
      fn()
    }
  }

  const set = (attribute: keyof typeof template, rawValue: string | number | boolean): void => {
    if (attribute === 'taxationScheme') {
      const scheme = Number(rawValue) as TAXATION_SCHEME
      const newInputs = inputs.copy()

      if (scheme === TAXATION_SCHEME.STANDARD) {
        newInputs.standardTaxationQuantity = newInputs.quantity
        newInputs.marginTaxationQuantity = 0
      } else {
        newInputs.marginTaxationQuantity = newInputs.quantity
        newInputs.standardTaxationQuantity = 0
      }

      onInputChange?.(newInputs)
      setInputs(newInputs)
      return
    }
    if (
      initializeFinishTypeAccordingCardNameOrFoil(card) !== FINISH_TYPE_LABELS.REGULAR &&
      //@ts-ignore
      FINISH_ATTRIBUTES.includes(attribute.replace('is', ''))
    ) {
      return
    }

    const newInputs = inputs.copy()
    let newValue = rawValue

    // 1. Simple Boolean toggle
    if (rawValue === 'toggle') {
      const key = attribute as keyof RichArticle
      const current = inputs[key]
      newValue = typeof current === 'boolean' ? !current : rawValue
    }

    // 2. FinishType toggle
    const finishTypeAliasMap: Record<string, FINISH_TYPE_LABELS> = {
      isFoil: FINISH_TYPE_LABELS.FOIL,
      isReverseHolo: FINISH_TYPE_LABELS.REVERSE_HOLO,
      isRainbowFoil: FINISH_TYPE_LABELS.RAINBOW_FOIL,
      isColdFoil: FINISH_TYPE_LABELS.COLD_FOIL,
    }

    if (attribute in finishTypeAliasMap) {
      const selectedLabel = finishTypeAliasMap[attribute]
      const isSelected = inputs.finishType === selectedLabel
      const nextFinishType = isSelected ? FINISH_TYPE_LABELS.REGULAR : selectedLabel

      newInputs.setAttribute('finishType', nextFinishType)

      // Object.values(finishTypeAliasMap).forEach((label) => {
      //   newInputs.setAttribute(label as any, label === nextFinishType)
      // })

      onInputChange?.(newInputs)
      setInputs(newInputs)
      return
    }

    // 3. CountryEdition toggle
    else if (attribute === 'countryEdition') {
      const current = inputs.countryEdition
      newValue = current === rawValue ? CountryEdition.Regular : rawValue
      newInputs.setAttribute('countryEdition', newValue)
    }

    // 4. Default fallback
    else {
      newInputs.setAttribute(attribute, newValue)
    }

    onInputChange?.(newInputs)
    setInputs(newInputs)
  }

  const changeQuantity = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (Number.isInteger(Number(e.target.value))) handleChange(e)
  }

  for (const hotkey of hotkeys) {
    const [key, parameter, value] = hotkey

    let parsedValue: string | number = value
    let parsedParameter: string = parameter
    if (parameter === 'language') {
      parsedValue = CardLanguage[value as keyof typeof CardLanguage]
      parsedParameter = 'idLanguage'
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScopelessHotkeys(
      key,
      (e: KeyboardEvent) =>
        hotkeyCallback(e, () => set(parsedParameter as keyof typeof template, parsedValue)),
      [inputs, disableHotkeys, initialInputs]
    )
  }
  useScopelessHotkeys(
    'Delete',
    (e) =>
      hotkeyCallback(e, () => {
        stopEdit()
      }),
    [inputs, disableHotkeys, initialInputs]
  )

  const confirmChanges = (e: KeyboardEvent, force = false) => {
    hotkeyCallback(
      e,
      () => {
        const quantity = Math.ceil(Number(inputs.quantity))
        if (!quantity || quantity < 1) return

        const locations = [
          {
            _id: '',
            userId: '',
            ptArticleId: 0,
            name: inputs.location,
            quantity: quantity,
            deltaQuantity: quantity,
            totalPickedQuantity: 0,
            totalQuantityWithIssues: 0,
          },
        ]

        const inventoryArticle = new InventoryArticle({
          ...inputs,
          quantity,
          newPrice: newPrice,
          locations,
          taxationScheme: selectedTaxationScheme !== '' ? selectedTaxationScheme : undefined,
          buyPrice: inputs.buyPrice,
          buyPriceLock: inputs.buyPrice !== undefined ? true : undefined,
          purchaseEventId: template?.purchaseEventId,
        })

        handleSave(inventoryArticle)
      },
      force
    )
  }

  useScopelessHotkeys('Space', (e) => confirmChanges(e), [inputs, disableHotkeys, initialInputs])

  useScopelessHotkeys('Enter', (e) => confirmChanges(e, true), [
    inputs,
    disableHotkeys,
    initialInputs,
  ])

  useScopelessHotkeys(
    'c',
    (e) =>
      hotkeyCallback(e, () => {
        const quantity = Math.ceil(Number(inputs.quantity))
        if (!quantity || quantity < 1) return
        handleSave(
          new InventoryArticle({
            ...inputs.toPlainObject(),
            quantity,
            price: initialInputs.price,
            comment: inputs.comment,
            idArticle: uuid(),
            taxationScheme: selectedTaxationScheme !== '' ? selectedTaxationScheme : undefined,
            purchaseEventId: template?.purchaseEventId,
          }),
          true
        )
        const newInputs = new RichArticle({
          ...inputs.toPlainObject(),
          price: initialInputs.price,
          idArticle: uuid(),
        })

        setInputs(newInputs)
      }),
    [inputs, disableHotkeys, initialInputs]
  )

  if (addByExp) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScopelessHotkeys('b', (e) => hotkeyCallback(e, () => handleSave('next')), [
      inputs,
      disableHotkeys,
    ])
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScopelessHotkeys('v', (e) => hotkeyCallback(e, () => handleSave('previous')), [
      inputs,
      disableHotkeys,
    ])
  }

  // this is for limiting the use of tab to the quantity and comment fields
  const focusableEls = [
    document.getElementById('article-edit-quantity'),
    document.getElementById('article-edit-comment'),
  ]
  const firstFocusableEl = focusableEls[0]
  const lastFocusableEl = focusableEls[focusableEls.length - 1]
  const KEYCODE_TAB = 9
  document.getElementById('article-edit-container')?.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' || e.keyCode === KEYCODE_TAB) {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl?.focus()
          e.preventDefault()
        }
      } else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl?.focus()
          e.preventDefault()
        }
      }
    }
  })

  return (
    <Box
      id="article-edit-container"
      key={
        //forces rerender when
        disableHotkeys ? 'lol' : 'mol'
      }
      padding={1}
    >
      <div className="ms-3 mb-4">
        <h4>{card.name}</h4>
        <h6 className="m-0 p-0">
          [{(card.expansion as unknown as Expansion)?.name || card.expansion}]
        </h6>
      </div>
      <div className="d-flex flex-lg-row flex-column justify-content-center align-items-lg-start align-items-center">
        <Box margin="0">
          <Box
            width={`${250}px`}
            height={`${250 * 1.39}px`}
            onClick={() => textFieldRef.current?.focus()}
            margin="0"
          >
            <VisualArticle
              displayOnly
              rowHeight={250 * 1.39}
              article={new InventoryArticle(inputs.toPlainObject())}
              currency={user?.currency}
              parentEl={ARTICLE_EDIT}
              visualArticleSizeClass="three"
            />
          </Box>
        </Box>
        <div className="mx-5">
          <HotkeyLegend
            article={inputs}
            onClick={() => textFieldRef.current?.focus()}
            addByExp={addByExp}
            disabled={!hasFocus}
            setInputs={setInputs}
            showExistingStock={!showExistingStock}
            template={template}
            onGradingModeChange={setGradingActive}
            setDisabledCommentTextfield={setDisabledCommentTextfield}
            setArticleIsGraded={setArticleIsGraded}
            set={set}
            onRefocus={() => textFieldRef.current?.focus()}
          />
        </div>

        <div>
          {addByExp && expansion && counter !== undefined && cardsInExpansion && setAnchorEl && (
            <Box display="flex" alignItems="center" justifyContent="center">
              <Box>
                <Typography>
                  {counter + 1}/{cardsInExpansion.length}
                </Typography>
              </Box>
              <Box marginLeft={2} width="200px">
                <Paper>
                  <Autocomplete<CardDataItem & { index: number }>
                    options={cardsInExpansion.map((card, index) => ({
                      ...card.toPlainObject(),
                      index,
                    }))}
                    //renderOption={(option) => option.name}
                    value={null}
                    autoHighlight={true}
                    data-testid="add-by-expansion-input"
                    getOptionLabel={(option) => `${option.name} ${option.cn ? option.cn : ''}`}
                    onChange={async (_e, newValue) => {
                      await sleep(50)

                      setCounter?.(newValue?.index || 0)
                      setAnchorEl(null)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoFocus
                        fullWidth
                        name={'jump-to-card-input'}
                        variant="outlined"
                        onFocus={(event) => {
                          event.target.select()
                        }}
                        label={t('addArticles.jumpTo')}
                      />
                    )}
                  />
                </Paper>
              </Box>
            </Box>
          )}
        </div>

        <div className="w-100" data-testid="edit-article" style={{ maxWidth: 300 }}>
          <Box data-testid="article-being-added-box">
            <div className="d-flex flex-column w-100">
              <TextField
                id="article-edit-quantity"
                key={inputs.id}
                autoFocus
                inputRef={textFieldRef}
                onFocus={(e) => {
                  e.target.select()
                  setHasFocus(true)
                }}
                onBlur={() => setHasFocus(false)}
                value={inputs.quantity}
                onChange={changeQuantity}
                margin="none"
                label={t('card.quantity')}
                name="quantity"
                fullWidth
                disabled={gradingActive}
                InputProps={{
                  inputProps: { min: 1 },
                  autoComplete: 'off',
                  readOnly: disabledQuantityTextfield,
                }}
                error={inputs.quantity <= 0}
                data-testid="quanty-input-add-new-article"
                helperText={
                  inputs.quantity <= 0 ? (
                    t('addArticles.invalidQuantity')
                  ) : hasFocus ? (
                    t('addArticles.hotkeysActive')
                  ) : (
                    <span style={{ color: palette.primary.main }}>
                      <b>{t('addArticles.clickFieldToEditArticle')}</b>
                    </span>
                  )
                }
              />

              <TextField
                value={inputs.price}
                onChange={handleChange}
                label={t('card.sellPrice')}
                margin="dense"
                type="number"
                name="price"
                data-testid="template-price"
                fullWidth
              />
              {user.featureFlags?.taxation && (
                <TextField
                  value={inputs.buyPrice ?? ''}
                  onChange={(e) => {
                    const newInputs = inputs.copy()
                    if (e.target.value === '') {
                      newInputs.buyPrice = undefined
                    } else {
                      newInputs.buyPrice = Number(e.target.value)
                    }
                    onInputChange?.(newInputs)
                    setInputs(newInputs)
                  }}
                  label={t('taxation.buyPrice')}
                  margin="dense"
                  type="number"
                  name="buyPrice"
                  data-testid="template-buy-price"
                  fullWidth
                />
              )}

              <TextField
                id="article-edit-location"
                value={inputs.location}
                onChange={handleChange}
                margin="none"
                label={t('card.location')}
                name="location"
                fullWidth
                data-testid="location-input-add-new-article"
              />
              <TextField
                id="article-edit-comment"
                value={inputs.comment}
                disabled={disabledCommentTextfield}
                onChange={handleChange}
                margin="none"
                label={t('card.comment')}
                name="comment"
                fullWidth
                data-testid="comment-input-add-new-article"
              />
            </div>

            {user.featureFlags?.taxation && user.enableMarginTaxation && (
              <FormControl fullWidth>
                <InputLabel className="taxation-edit-label">
                  {t('taxation.taxationScheme')}
                </InputLabel>
                <Select
                  label={t('taxation.taxationScheme')}
                  fullWidth
                  value={selectedTaxationScheme}
                  name="taxationScheme"
                  data-testid="template-taxation-schema"
                  disabled={!user.enableMarginTaxation}
                  onChange={(e) => {
                    const scheme = Number(e.target.value) as TAXATION_SCHEME
                    setSelectedTaxationScheme(scheme)
                    set('taxationScheme', scheme)
                  }}
                  className="my-2"
                >
                  {TAXATION_SCHEME_CONVERSIONS.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </div>
      </div>
      <div className="d-flex w-100 justify-content-end">
        <h6>{t('addArticles.confirmEdits')}</h6>
      </div>

      {anchorEl && cardsInExpansion && (
        <ClickAwayListener
          onClickAway={() => {
            setAnchorEl(null)
          }}
        >
          <Popper
            open={!!anchorEl}
            anchorEl={anchorEl}
            disablePortal
            style={{ zIndex: zIndex.modal }}
          >
            <Box width="200px">
              <Paper>
                <Autocomplete<CardDataItem & { index: number }>
                  options={cardsInExpansion.map((card, index) => ({
                    ...card.toPlainObject(),
                    index,
                  }))}
                  //renderOption={(option) => option.name}
                  value={null}
                  autoHighlight={true}
                  data-testid="add-by-expansion-input"
                  getOptionLabel={(option) => `${option.name} ${option.cn ? option.cn : ''}`}
                  onChange={async (_e, newValue) => {
                    await sleep(50)

                    setCounter?.(newValue?.index || 0)
                    setAnchorEl(null)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus
                      fullWidth
                      name={'jump-to-card-input'}
                      variant="outlined"
                      onFocus={(event) => {
                        event.target.select()
                      }}
                    />
                  )}
                />
              </Paper>
            </Box>
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  )
}
